import { useCallback, useEffect, useRef, useState } from 'react';
import useBodyScrollLock from './useBodyScrollLock';
import useHoverOpenIntent from './useHoverOpenIntent';

/**
 * Owns the featured nav bar's mega-menu lifecycle: which menu (if any) is
 * open, which menus have ever been mounted, hover-intent open timing, and
 * body scroll lock while a menu is open. Also exposes handleClose via
 * closeMegaMenuRef so a sibling control (the drawer's hamburger button) can
 * dismiss an open mega menu before opening the drawer.
 *
 * @param {NavigationItem[]} featuredItems
 * @param {{ current: (() => void)|null }} [closeMegaMenuRef]
 * @returns {{
 *   openMegaMenuId: string|null,
 *   megaMenuItems: NavigationItem[],
 *   hoverIntent: { onMouseEnter: () => void, onMouseLeave: () => void, onFocus: () => void, onBlur: () => void },
 *   handleClose: () => void,
 *   handleItemClick: (item: NavigationItem) => void,
 *   handleItemHoverEnter: (item: NavigationItem) => void,
 *   handleItemHoverFocus: (item: NavigationItem) => void
 * }}
 */
const useFeaturedMegaMenuState = (featuredItems, closeMegaMenuRef) => {
	const [openMegaMenuId, setOpenMegaMenuId] = useState(null);

	// Ids of mega menus that have been opened at least once. A mega menu is
	// only rendered (see megaMenuItems below) once its id lands here — lazily
	// mounting avoids fetching/rendering every featured item's mega menu
	// content (including any embedded fragments) up front, before the user
	// has opened any of them. Never pruned on close, so re-opening a menu
	// doesn't re-trigger its mount-time work (e.g. fragment fetches).
	const [mountedMenuIds, setMountedMenuIds] = useState(new Set());

	const hoverItemIdRef = useRef(null);

	const handleClose = useCallback(() => {
		setOpenMegaMenuId(null);
	}, []);

	const handleOpen = useCallback((itemId) => {
		setOpenMegaMenuId(itemId);
		setMountedMenuIds((ids) => {
			if (ids.has(itemId)) return ids;
			return new Set(ids).add(itemId);
		});
	}, []);

	const handleHoverOpen = useCallback(() => {
		if (hoverItemIdRef.current) handleOpen(hoverItemIdRef.current);
	}, [handleOpen]);

	const handleItemClick = useCallback((item) => {
		setOpenMegaMenuId((prev) => {
			const next = prev === item.id ? null : item.id;
			if (next && item.columns?.length > 0) {
				setMountedMenuIds((ids) => {
					if (ids.has(next)) return ids;
					return new Set(ids).add(next);
				});
			}
			return next;
		});
	}, []);

	const hoverIntent = useHoverOpenIntent({ onOpen: handleHoverOpen });

	const handleItemHoverEnter = useCallback(
		(item) => {
			hoverItemIdRef.current = item.id;
			hoverIntent.onMouseEnter();
		},
		[hoverIntent]
	);

	const handleItemHoverFocus = useCallback(
		(item) => {
			hoverItemIdRef.current = item.id;
			hoverIntent.onFocus();
		},
		[hoverIntent]
	);

	useBodyScrollLock(openMegaMenuId !== null);

	useEffect(() => {
		if (closeMegaMenuRef) {
			// eslint-disable-next-line no-param-reassign -- ref.current assignment is idiomatic React
			closeMegaMenuRef.current = handleClose;
		}
	}, [closeMegaMenuRef, handleClose]);

	const megaMenuItems = featuredItems.filter(
		(item) => mountedMenuIds.has(item.id) && item.columns?.length > 0
	);

	return {
		openMegaMenuId,
		megaMenuItems,
		hoverIntent,
		handleClose,
		handleItemClick,
		handleItemHoverEnter,
		handleItemHoverFocus
	};
};

export default useFeaturedMegaMenuState;
